import { Alert, Text } from "@mantine/core";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import dayjs from "dayjs";
import type {
  AssignmentRecoveryState,
  ReassignmentContext,
  ReassignmentWarning,
  StaleAssignmentConflict,
} from "../../types/casePathologistAssignment";

interface ReplacePathologistModalAlertsProps {
  context: ReassignmentContext;
  staleConflict?: StaleAssignmentConflict;
  recoveryState: AssignmentRecoveryState;
  warnings: ReassignmentWarning[];
}

export default function ReplacePathologistModalAlerts({
  context,
  staleConflict,
  recoveryState,
  warnings,
}: ReplacePathologistModalAlertsProps) {
  return (
    <>
      {context.activeReview.acknowledgementRequired &&
        context.activeReview.reviewer && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="yellow"
            variant="light"
            title="Active review in progress"
            role="status"
          >
            {context.activeReview.reviewer.fullName} has an active review
            session
            {context.activeReview.expiresAt
              ? ` until ${dayjs(context.activeReview.expiresAt).format(
                  "h:mm A"
                )}`
              : ""}
            . Replacing the pathologist will interrupt that session.
          </Alert>
        )}

      {staleConflict && (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="yellow"
          variant="light"
          title="Assignment changed"
          role="alert"
        >
          Current pathologist is{" "}
          {staleConflict.currentAssignment.pathologist.fullName}
          {` (revision ${staleConflict.currentAssignment.revision}). `}
          Review the updated assignment before submitting again. Your selected
          pathologist and reason have been preserved.
        </Alert>
      )}

      {recoveryState.status !== "idle" && recoveryState.message && (
        <Alert
          icon={<IconInfoCircle size={18} />}
          color={recoveryState.status === "unresolved" ? "red" : "yellow"}
          variant="light"
          title={
            recoveryState.status === "reconciling"
              ? "Checking replacement result"
              : "Replacement result checked"
          }
          role="status"
        >
          {recoveryState.message}
        </Alert>
      )}

      {warnings.map((warning) => (
        <Alert
          key={`${warning.code}-${warning.message}`}
          icon={<IconAlertCircle size={18} />}
          color={warning.severity === "warning" ? "yellow" : "blue"}
          variant="light"
          title={warning.severity === "warning" ? "Attention" : "Information"}
          role="status"
        >
          <Text size="sm">{warning.message}</Text>
          {warning.followUp && (
            <Text size="sm" mt={4}>
              {warning.followUp}
            </Text>
          )}
        </Alert>
      ))}

      {context.relatedResponsibilities.map((responsibility) => (
        <Alert
          key={`${responsibility.kind}-${
            responsibility.id ?? responsibility.label
          }`}
          icon={<IconInfoCircle size={18} />}
          color="blue"
          variant="light"
          title="Related responsibility"
          role="status"
        >
          {responsibility.label}
          {responsibility.assignee && ` — ${responsibility.assignee.fullName}`}
        </Alert>
      ))}
    </>
  );
}
